import { getDb, STORE_PENDING_CANJES } from './db';

/**
 * Cola local de canjes.
 *
 * POR QUÉ EXISTE: son mercados mayoristas y la señal falla. Si registrar un canje exigiera
 * conexión, el piloto fracasaría el primer día y no por el diseño del formulario.
 *
 * POR QUÉ ES UN STORE APARTE y no una generalización de `offline/queue.ts`: esa cola está
 * tipada para asistencia y es el camino más crítico del sistema, ya en producción con gente
 * marcando de verdad. Generalizarla para dos formas de dato muy distintas —un marcado tiene una
 * foto y banderas; un canje tiene líneas, premios y dos fotos— es arriesgar el marcado para
 * ahorrar un archivo. Se replica el patrón probado: idempotencia por `clientUuid`, `attempts`
 * que solo cuentan los rechazos definitivos, y `status` `pendiente`/`fallido`.
 */

/** El canje tal como se guarda localmente: sin los paths, que se obtienen al subir las fotos. */
export interface CanjeParaRegistrar {
  clientUuid: string;
  campaignId: string;
  pdvId: string;
  /** `null` cuando no había programación: el servidor lo marca como fuera de programación. */
  assignmentId: string | null;
  fecha: string;
  ocurridoEn: string | null;
  clienteNombre: string | null;
  clienteContacto: string | null;
  montoCompra: number | null;
  marcasDejadas: string[];
  observaciones: string | null;
  /**
   * Número de boleta tipeado (0043). Va en la cola como un dato más y no como la foto: es texto,
   * así que sobrevive a la serialización sin nada especial y llega igual con o sin señal.
   */
  boletaNumero: string | null;
  lineas: {
    dynamicId: string;
    canjes: number;
    premios: { prizeId: string; units: number; equivalencia?: boolean }[];
  }[];
}

export interface PendingCanje {
  clientUuid: string;
  input: CanjeParaRegistrar;
  /**
   * La foto de la boleta es OPCIONAL desde la 0036. Hay tiendas que no dejan usar el celular en
   * sala, y exigirla convertía una restricción de la tienda en un canje que no se puede registrar.
   */
  boletaBlob: Blob | null;
  /** La foto del premio es opcional: no todas las dinámicas la exigen. */
  premioBlob: Blob | null;
}

export interface QueuedCanje extends PendingCanje {
  attempts: number;
  lastError: string | null;
  status: 'pendiente' | 'fallido';
}

/** Rechazos definitivos consecutivos tras los cuales el canje pasa a `fallido`. */
export const MAX_CANJE_ATTEMPTS = 5;

/** Puerto de envío. Lo implementa `api/canjes.ts`, que se registra al importarse. */
export interface CanjeUploader {
  uploadCanjePhoto(clientUuid: string, tipo: 'boleta' | 'premio', photo: Blob): Promise<string>;
  registrarCanje(
    input: CanjeParaRegistrar & { fotoBoletaPath: string | null; fotoPremioPath: string | null },
  ): Promise<string>;
  /** true si el servidor rechazó DEFINITIVAMENTE (4xx/RLS). Un fallo de red devuelve false. */
  isPermanentError(err: unknown): boolean;
}

let uploader: CanjeUploader | null = null;

/** Registra el enviador real (lo llama `api/canjes.ts` al importarse). */
export function setCanjeUploader(u: CanjeUploader): void {
  uploader = u;
}

/** Guarda un canje en la cola local. Idempotente por `clientUuid`: si ya estaba, conserva su
 *  historial de reintentos y solo actualiza los datos. */
export async function enqueueCanje(p: PendingCanje): Promise<void> {
  const db = await getDb();
  const existing = await db.get(STORE_PENDING_CANJES, p.clientUuid);
  await db.put(STORE_PENDING_CANJES, {
    ...p,
    attempts: existing?.attempts ?? 0,
    lastError: existing?.lastError ?? null,
    status: existing?.status ?? 'pendiente',
  });
}

/** Canjes que todavía esperan subir. Los `fallido` NO cuentan: ya no se reintentan. */
export async function listPendingCanjes(): Promise<QueuedCanje[]> {
  const db = await getDb();
  const all = await db.getAll(STORE_PENDING_CANJES);
  return all.filter((i) => i.status !== 'fallido');
}

/** Canjes que agotaron sus intentos. Se le muestran a la persona con su motivo. */
export async function listFailedCanjes(): Promise<QueuedCanje[]> {
  const db = await getDb();
  const all = await db.getAll(STORE_PENDING_CANJES);
  return all.filter((i) => i.status === 'fallido');
}

export async function pendingCanjeCount(): Promise<number> {
  return (await listPendingCanjes()).length;
}

export async function discardPendingCanje(clientUuid: string): Promise<void> {
  const db = await getDb();
  await db.delete(STORE_PENDING_CANJES, clientUuid);
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Sube todos los canjes pendientes: fotos a Storage y después la RPC con sus paths.
 *
 * El orden importa: si la RPC se llamara antes de tener los paths, un canje quedaría guardado
 * apuntando a fotos que no existen. La RPC es idempotente por `client_uuid`, así que un
 * reintento después de que la foto ya subió no duplica nada.
 *
 * Un fallo de RED no consume intentos y los reinicia: estar sin señal en un mercado no es culpa
 * de la mercadista. Solo los rechazos definitivos del servidor gastan los 5 intentos, y al
 * agotarlos el canje sale de los pendientes (se apaga el aviso eterno) y queda visible como
 * fallido con su motivo.
 */
export async function flushCanjes(): Promise<{ sent: number; remaining: number }> {
  const db = await getDb();
  const items = (await db.getAll(STORE_PENDING_CANJES)).filter((i) => i.status !== 'fallido');
  if (items.length === 0) return { sent: 0, remaining: 0 };
  if (!uploader) {
    throw new Error('La cola de canjes no tiene enviador registrado (importa api/canjes).');
  }

  let sent = 0;
  for (const item of items) {
    try {
      const fotoBoletaPath = item.boletaBlob
        ? await uploader.uploadCanjePhoto(item.clientUuid, 'boleta', item.boletaBlob)
        : null;
      const fotoPremioPath = item.premioBlob
        ? await uploader.uploadCanjePhoto(item.clientUuid, 'premio', item.premioBlob)
        : null;

      // `clientUuid` se fuerza desde la CLAVE del store, no del payload guardado: es lo que
      // hace idempotente el reintento en el servidor. Si los dos valores pudieran divergir
      // —por un guardado a medias o un cambio futuro en el formulario— un reintento crearía un
      // canje duplicado, y un canje duplicado es producto entregado dos veces en el reporte.
      await uploader.registrarCanje({
        ...item.input,
        clientUuid: item.clientUuid,
        fotoBoletaPath,
        fotoPremioPath,
      });
      await db.delete(STORE_PENDING_CANJES, item.clientUuid);
      sent += 1;
    } catch (err) {
      const permanent = uploader.isPermanentError(err);
      const attempts = permanent ? item.attempts + 1 : 0;
      await db.put(STORE_PENDING_CANJES, {
        ...item,
        attempts,
        lastError: errorMessage(err),
        status: permanent && attempts >= MAX_CANJE_ATTEMPTS ? 'fallido' : 'pendiente',
      });
    }
  }

  const remaining = (await listPendingCanjes()).length;
  return { sent, remaining };
}
